import {
  postJsMessage,
  registerSpecificNotificationHandler,
  unregisterSpecificNotificationHandler,
} from '../../bridge';
import API from '../../api';
import { PostMessageOption } from '../../types';
import { isAvailable, ERROR_CODE, genError } from '../utils';
import { Platform } from '../../utils/platform';
import { openSysDlgSelectDirectory, openSysDlgSelectPath } from '../filesystem';
import { UnifyWebviewPathIntention } from '../filesystem/types';
import { APIKeys } from '../../api/version';
import { NativeAPIResponseMessage } from '@zoom/hybrid-jsbridge';

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * download.startDownload
 *
 * [PLATFORM]
 * mac        6.0.5+
 * windows    6.0.5+
 * linux      6.0.5+
 * ios        6.3.0+
 * android    6.3.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function startDownload(
  params: DownloadRequest,
  options?: PostMessageOption
): Promise<NativeAPIResponseMessage<StartDownloadResponse>> {
  await isAvailable('download.startDownload');
  const { url, useDefaultDownloadPath, defaultFileName } = params;

  if (!url) {
    return Promise.reject(
      genError({
        errorCode: ERROR_CODE.REQUEST_PARAM_ERROR,
        apiName: API.startDownload,
        message: `url can't be empty!`,
      })
    );
  }

  if (!params.pathId) {
    if (
      !useDefaultDownloadPath &&
      (Platform.isMacOS() || Platform.isLinux() || Platform.isWindows())
    ) {
      const res = await openSysDlgSelectPath(
        defaultFileName,
        UnifyWebviewPathIntention.Write
      );

      if (res.returnCode !== 0) {
        return Promise.reject(
          genError({
            errorCode: ERROR_CODE.CANCEL_SELECT_PATH,
            apiName: API.startDownload,
            message: `User canceled path selection, pathId is empty!`,
          })
        );
      }

      const pathId = res.result.pathId;
      if (!pathId) {
        return Promise.reject(
          genError({
            errorCode: ERROR_CODE.REQUEST_PARAM_ERROR,
            apiName: API.startDownload,
            message: `pathId can't be empty!`,
          })
        );
      }

      params.pathId = pathId;
    } else {
      params.pathId = '';
    }
  }
  const response = await postJsMessage<DownloadRequest, StartDownloadResponse>(
    API.startDownload,
    { ...params },
    options
  );
  if (response.returnCode === 0) {
    response.result.pathId = params.pathId;
  }
  return response;
}

export type DownloadRequest = {
  url: string;
  defaultFileName: string;
  needZak: boolean;
  pathId?: string;
  method:
    | 'GET'
    | 'POST'
    | 'PUT'
    | 'DELETE'
    | 'HEAD'
    | 'OPTIONS'
    | 'PATCH'
    | 'TRACE'
    | 'CONNECT';
  headers?: Record<string, string>;
  cookies?: Record<string, string>;
  useDefaultDownloadPath: boolean;
};

export type StartDownloadResponse = {
  downloadId: string;
  pathId: string;
};

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * download.pauseDownload
 *
 * [PLATFORM]
 * mac        6.0.5+
 * windows    6.0.5+
 * linux      6.0.5+
 * ios        6.3.0+
 * android    6.3.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function pauseDownload(
  downloadId: string,
  options?: PostMessageOption
): Promise<NativeAPIResponseMessage<PauseDownloadResponse>> {
  await validateDownloadId('download.pauseDownload', downloadId);
  return postJsMessage<PauseDownloadRequest, PauseDownloadResponse>(
    API.pauseDownload,
    { downloadId },
    options
  );
}

export type PauseDownloadRequest = {
  downloadId: string;
};

export type PauseDownloadResponse = {
  errorCode: number; // 0-success
  errorMessage: string;
};

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * download.resumeDownload
 *
 * [PLATFORM]
 * mac        6.0.5+
 * windows    6.0.5+
 * linux      6.0.5+
 * ios        6.3.0+
 * android    6.3.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function resumeDownload(
  downloadId: string,
  options?: PostMessageOption
): Promise<NativeAPIResponseMessage<ResumeDownloadResponse>> {
  await validateDownloadId('download.resumeDownload', downloadId);
  return postJsMessage<ResumeDownloadRequest, ResumeDownloadResponse>(
    API.resumeDownload,
    { downloadId },
    options
  );
}

export type ResumeDownloadRequest = {
  downloadId: string;
};

export type ResumeDownloadResponse = {
  errorCode: number; // 0-success
  errorMessage: string;
};

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * download.cancelDownload
 *
 * [PLATFORM]
 * mac        6.0.5+
 * windows    6.0.5+
 * linux      6.0.5+
 * ios        6.3.0+
 * android    6.3.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function cancelDownload(
  downloadId: string,
  options?: PostMessageOption
): Promise<NativeAPIResponseMessage<CancelDownloadResponse>> {
  await validateDownloadId('download.cancelDownload', downloadId);
  offDownloadProgress(downloadId);
  return postJsMessage<CancelDownloadRequest, CancelDownloadResponse>(
    API.cancelDownload,
    { downloadId },
    options
  );
}

export type CancelDownloadRequest = {
  downloadId: string;
};

export type CancelDownloadResponse = {
  errorCode: number; // 0-success
  errorMessage: string;
};

export type GetDownloadInfoRequest = {
  downloadId: string;
};

export type GetDownloadInfoResponse = {
  returnCode: number; // 0-success
  errorMsg?: string;
  result: {
    downloadedBytes: number;
    percent: number; // 0-100
    totalSizeInBytes: number;
  };
};
/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * download.getDownloadInfo
 *
 * [PLATFORM]
 * mac        6.0.5+
 * windows    6.0.5+
 * linux      6.0.5+
 * ios        6.3.0+
 * android    6.3.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function getDownloadInfo(
  downloadId: string,
  options?: PostMessageOption
): Promise<NativeAPIResponseMessage<GetDownloadInfoResponse>> {
  await validateDownloadId('download.getDownloadInfo', downloadId);
  return postJsMessage<GetDownloadInfoRequest, GetDownloadInfoResponse>(
    API.getDownloadInfo,
    { downloadId },
    options
  );
}

class DownloadProgressManager {
  private listeners: Record<
    string,
    (message: DownloadCallbackMessage) => void
  > = {};

  add(
    downloadId: string,
    callback: (message: DownloadCallbackMessage) => void
  ): void {
    const isEmpty = Object.keys(this.listeners).length === 0;

    if (isEmpty) {
      registerSpecificNotificationHandler(
        'UnifyNotif_OnDownloadUpdate',
        this.dispatch
      );
    }

    this.listeners[downloadId] = callback;
  }

  remove(downloadId: string): void {
    if (this.listeners[downloadId]) {
      delete this.listeners[downloadId];
    }

    if (Object.keys(this.listeners).length === 0) {
      unregisterSpecificNotificationHandler(
        'UnifyNotif_OnDownloadUpdate',
        this.dispatch
      );
    }
  }

  private dispatch = (message: DownloadCallbackMessage) => {
    const { returnCode, result } = message;
    const { downloadId, percent } = result;

    const callback = this.listeners[downloadId];
    if (callback) {
      try {
        callback(message);
      } catch (e) {
        console.error(
          `[@zoom/hybrid-jssdk] hybrid.download.onDownloadProgress callback failed.`,
          e
        );
      }
    }

    if (returnCode !== 0 || percent === 100) {
      this.remove(downloadId);
    }
  };
}

const downloadProgressManager = new DownloadProgressManager();

// --------------------------- Public API ---------------------------

export type DownloadCallbackMessage = {
  returnCode: number; // 0-success
  errorMsg?: string;
  result: {
    pathId?: string;
    notificationName: string;
    downloadId: string;
    downloadedBytes: number;
    percent: number; // 0-100
    totalSizeInBytes: number;
  };
};
/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * download.onDownloadProgress
 *
 * [PLATFORM]
 * mac        6.0.5+
 * windows    6.0.5+
 * linux      6.0.5+
 * ios        6.3.0+
 * android    6.3.0+
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export function onDownloadProgress(
  downloadId: string | string[],
  downloadCallback: (message: DownloadCallbackMessage) => void
): void {
  if (Array.isArray(downloadId)) {
    downloadId.forEach((id) => {
      downloadProgressManager.add(id, downloadCallback);
    });
  } else {
    downloadProgressManager.add(downloadId, downloadCallback);
  }
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * download.offDownloadProgress
 *
 * [PLATFORM]
 * mac        6.0.5+
 * windows    6.0.5+
 * linux      6.0.5+
 * ios        6.3.0+
 * android    6.3.0+
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export function offDownloadProgress(downloadId: string | string[]): void {
  if (Array.isArray(downloadId)) {
    downloadId.forEach((id) => {
      downloadProgressManager.remove(id);
    });
  } else {
    downloadProgressManager.remove(downloadId);
  }
}

async function validateDownloadId(
  apiName: APIKeys,
  downloadId: string
): Promise<void> {
  await isAvailable(apiName);
  if (!downloadId) {
    throw genError({
      errorCode: ERROR_CODE.REQUEST_PARAM_ERROR,
      apiName,
      message: `downloadId can't be empty!`,
    });
  }
}

export type BatchDownloadResponse = Array<{
  returnCode: number;
  errorMsg?: string;
  result?: StartDownloadResponse;
  originalRequest: DownloadRequest;
}>;

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * download.batchStartDownload
 *
 * [PLATFORM]
 * mac        6.0.5+
 * windows    6.0.5+
 * linux      6.0.5+
 * ios        6.3.0+
 * android    6.3.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function batchStartDownload(
  requests: DownloadRequest[],
  options?: PostMessageOption
): Promise<BatchDownloadResponse> {
  await isAvailable('download.startDownload');

  if (!requests?.length) {
    return Promise.reject(
      genError({
        errorCode: ERROR_CODE.REQUEST_PARAM_ERROR,
        apiName: API.startDownload,
        message: `Download requests array cannot be empty`,
      })
    );
  }

  // Check for pathId requirements
  const needsPathSelection = requests.some(
    (req) =>
      !req.pathId &&
      !req.useDefaultDownloadPath &&
      (Platform.isMacOS() || Platform.isLinux() || Platform.isWindows())
  );

  let sharedPathId = '';
  if (needsPathSelection) {
    try {
      const pathRes = await openSysDlgSelectDirectory(
        UnifyWebviewPathIntention.Write
      );

      if (pathRes.returnCode !== 0 || !pathRes.result.pathId) {
        return requests.map((req) => ({
          returnCode: ERROR_CODE.CANCEL_SELECT_PATH,
          errorMsg: 'User canceled path selection',
          originalRequest: req,
        }));
      }

      sharedPathId = pathRes.result.pathId;
    } catch (error) {
      return requests.map((req) => ({
        returnCode: ERROR_CODE.UNKNOWN_ERROR,
        errorMsg:
          error instanceof Error ? error.message : 'Path selection failed',
        originalRequest: req,
      }));
    }
  }

  // Process requests with resolved pathId
  const processedRequests = requests.map((req) => {
    if (!req.pathId && !req.useDefaultDownloadPath && sharedPathId) {
      return { ...req, pathId: sharedPathId };
    }
    return req;
  });

  // Execute downloads with error handling
  const downloadPromises = processedRequests.map((request) =>
    startDownload(request, options)
      .then((response) => ({
        returnCode: response.returnCode,
        result: response.result,
        errorMsg: response.errorMsg,
        originalRequest: request,
      }))
      .catch((error) => ({
        returnCode: error.errorCode || ERROR_CODE.UNKNOWN_ERROR,
        errorMsg: error.message || 'Unknown download error',
        originalRequest: request,
        result: undefined,
      }))
  );

  return Promise.all(downloadPromises);
}

async function runBatchOperation<T>(
  ids: string[],
  singleActionFn: (id: string) => Promise<T>,
  apiName: APIKeys
): Promise<{
  successes: Array<{ id: string; result: T }>;
  failures: Array<{ id: string; error: Error }>;
}> {
  await isAvailable(apiName);

  const results = await Promise.allSettled(
    ids.map((id) => singleActionFn(id).then((result) => ({ id, result })))
  );

  const successes: Array<{ id: string; result: T }> = [];
  const failures: Array<{ id: string; error: Error }> = [];

  for (let i = 0; i < results.length; i++) {
    const r = results[i];
    if (r.status === 'fulfilled') {
      successes.push(r.value);
    } else {
      failures.push({
        id: ids[i],
        error:
          r.reason instanceof Error ? r.reason : new Error(String(r.reason)),
      });
    }
  }

  return { successes, failures };
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * download.batchPauseDownload
 *
 * [PLATFORM]
 * mac        6.0.5+
 * windows    6.0.5+
 * linux      6.0.5+
 * ios        6.3.0+
 * android    6.3.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function batchPauseDownload(ids: string[]): Promise<{
  successes: Array<{
    id: string;
    result: NativeAPIResponseMessage<PauseDownloadResponse>;
  }>;
  failures: Array<{ id: string; error: Error }>;
}> {
  return runBatchOperation(ids, pauseDownload, 'download.pauseDownload');
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * download.batchResumeDownload
 *
 * [PLATFORM]
 * mac        6.0.5+
 * windows    6.0.5+
 * linux      6.0.5+
 * ios        6.3.0+
 * android    6.3.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function batchResumeDownload(ids: string[]): Promise<{
  successes: Array<{
    id: string;
    result: NativeAPIResponseMessage<ResumeDownloadResponse>;
  }>;
  failures: Array<{ id: string; error: Error }>;
}> {
  return runBatchOperation(ids, resumeDownload, 'download.resumeDownload');
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * download.batchCancelDownload
 *
 * [PLATFORM]
 * mac        6.0.5+
 * windows    6.0.5+
 * linux      6.0.5+
 * ios        6.3.0+
 * android    6.3.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function batchCancelDownload(ids: string[]): Promise<{
  successes: Array<{
    id: string;
    result: NativeAPIResponseMessage<CancelDownloadResponse>;
  }>;
  failures: Array<{ id: string; error: Error }>;
}> {
  return runBatchOperation(ids, cancelDownload, 'download.cancelDownload');
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * download.batchGetDownloadInfo
 *
 * [PLATFORM]
 * mac        6.0.5+
 * windows    6.0.5+
 * linux      6.0.5+
 * ios        6.3.0+
 * android    6.3.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function batchGetDownloadInfo(ids: string[]): Promise<{
  successes: Array<{
    id: string;
    result: NativeAPIResponseMessage<GetDownloadInfoResponse>;
  }>;
  failures: Array<{ id: string; error: Error }>;
}> {
  return runBatchOperation(ids, getDownloadInfo, 'download.getDownloadInfo');
}

export default {
  startDownload,
  pauseDownload,
  resumeDownload,
  cancelDownload,
  getDownloadInfo,
  onDownloadProgress,
  offDownloadProgress,
  batchStartDownload,
  batchPauseDownload,
  batchCancelDownload,
  batchResumeDownload,
  batchGetDownloadInfo,
};
